import React from 'react';
import { Badge } from '../../components/common';

/** Display only — `Number()` never feeds anything that is saved. */
const inr = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' });
export const formatINR = (v) => inr.format(Number(v || 0));

export const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
export const normalizeGstin = (value = '') => value.toUpperCase().replace(/\s+/g, '');

const pad = (n) => String(n).padStart(2, '0');

/**
 * `YYYY-MM-DD` in the browser's timezone. toISOString() would be UTC, which
 * in India is still "yesterday" until 05:30.
 */
export const toISODate = (d = new Date()) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const todayISO = () => toISODate(new Date());

export const addDaysISO = (iso, days) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  return toISODate(new Date(y, m - 1, d + Number(days || 0)));
};

/** `2025-12-09` → `09/12/2025` */
export const formatDMY = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return d && m && y ? `${d}/${m}/${y}` : iso;
};

/** Indian financial year (April–March) containing `date`, e.g. `2025-26`. */
export const financialYearOf = (date = new Date()) => {
  const start = date.getMonth() >= 3 ? date.getFullYear() : date.getFullYear() - 1;
  return `${start}-${String((start + 1) % 100).padStart(2, '0')}`;
};

/** The current FY and the `count - 1` before it, newest first. */
export const financialYearOptions = (count = 5) => {
  const start = Number(financialYearOf().slice(0, 4));
  return Array.from({ length: count }, (_, i) => {
    const y = start - i;
    return `${y}-${String((y + 1) % 100).padStart(2, '0')}`;
  });
};

export const INVOICE_STATUSES = [
  { value: 'draft', label: 'Draft', variant: 'default' },
  { value: 'issued', label: 'Issued', variant: 'primary' },
  { value: 'partially_paid', label: 'Partially paid', variant: 'warning' },
  { value: 'paid', label: 'Paid', variant: 'success' },
  { value: 'cancelled', label: 'Cancelled', variant: 'danger' },
];

export const PI_CANCEL_DESCRIPTION =
  'The proforma invoice keeps its number and is marked CANCELLED. To send a revised one, duplicate it as a new draft.';

/** Proforma invoices have no payments, so only these three. InvoiceStatusBadge renders them. */
export const PROFORMA_STATUSES = INVOICE_STATUSES.filter((s) => ['draft', 'issued', 'cancelled'].includes(s.value));

const STATUS_BY_VALUE = Object.fromEntries(INVOICE_STATUSES.map((s) => [s.value, s]));

export const InvoiceStatusBadge = ({ status, isOverdue = false, size = 'small' }) => {
  const meta = STATUS_BY_VALUE[status] ?? { label: status, variant: 'default' };
  return (
    <span className="bl-status-badges">
      <Badge variant={meta.variant} size={size}>{meta.label}</Badge>
      {isOverdue && <Badge variant="orange" size={size}>Overdue</Badge>}
    </span>
  );
};

export const PAYMENT_MODES = [
  { value: 'bank_transfer', label: 'Bank transfer' },
  { value: 'upi', label: 'UPI' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'cash', label: 'Cash' },
  { value: 'other', label: 'Other' },
];

export const paymentModeLabel = (mode) => PAYMENT_MODES.find((m) => m.value === mode)?.label ?? mode;

/**
 * Laravel keys array errors by index (`items.0.price`). Split them into
 * `{ fields, rows: { 0: { price } } }` so a table can show them per line.
 */
export const splitRowErrors = (fieldErrors, prefix = 'items') => {
  const fields = {};
  const rows = {};
  Object.entries(fieldErrors).forEach(([key, message]) => {
    const match = key.match(new RegExp(`^${prefix}\\.(\\d+)\\.(\\w+)$`));
    if (match) {
      const [, index, field] = match;
      rows[index] = { ...rows[index], [field]: message };
    } else {
      fields[key] = message;
    }
  });
  return { fields, rows };
};

/** Every message in a 422, flattened — for errors that come back as a list. */
export const getAllApiErrors = (error) => {
  const errors = error?.response?.data?.errors;
  if (!errors || typeof errors !== 'object') return [];
  return Object.values(errors).flatMap((value) => (Array.isArray(value) ? value : [String(value)]));
};
